/**
 * Vocabulaire et règles du catalogue : producteurs, produits, recherche.
 *
 * Tout est pur et sans dépendance Angular, comme `tiers.ts` et `reviews.ts` :
 * la recherche est la règle la plus facile à casser sans s'en apercevoir, elle
 * doit donc être testable sans monter un écran.
 *
 * Les montants sont en centimes entiers, jamais en nombres flottants — même
 * raison qu'au lot 8 : `0.1 + 0.2` ne vaut pas `0.3` en binaire.
 */

import { type Coordinates, distanceBetween } from './geo';
import { formatEurFromCents } from './tiers';

/**
 * Rayon d'un produit, identifié par une chaîne libre.
 *
 * Ce n'est pas une union fermée : un producteur qui vend du safran, du vin ou
 * des quenelles n'a pas à attendre une release. Les libellés connus vivent
 * dans `CATEGORIES` ; un identifiant encore absent y apparaît tel quel, et
 * les filtres se construisent à partir de ce qui est réellement en vente.
 */
export type CategoryId = string;

export interface Category {
  readonly id: CategoryId;
  readonly label: string;
}

/**
 * Dictionnaire des rayons déjà rencontrés, pas un plafond.
 *
 * L'ordre est celui des filtres. En ajouter un ici donne un libellé soigné ;
 * l'oublier n'empêche pas de vendre le produit.
 */
export const CATEGORIES: readonly Category[] = [
  { id: 'fruits', label: 'Fruits' },
  { id: 'legumes', label: 'Légumes' },
  { id: 'fromages', label: 'Fromages' },
  { id: 'cremerie', label: 'Crèmerie' },
  { id: 'oeufs', label: 'Œufs' },
  { id: 'viande', label: 'Viande' },
  { id: 'charcuterie', label: 'Charcuterie' },
  { id: 'pain', label: 'Pain' },
  { id: 'biscuits', label: 'Biscuits & pâtisserie' },
  { id: 'miel', label: 'Miel & confitures' },
  { id: 'epicerie', label: 'Épicerie' },
  { id: 'boissons', label: 'Boissons' },
  { id: 'vins', label: 'Vins' },
];

export function categoryLabel(id: CategoryId): string {
  return CATEGORIES.find((category) => category.id === id)?.label ?? id;
}

/**
 * Unité de vente, libre comme la catégorie.
 *
 * Le dictionnaire ci-dessous soigne les libellés fréquents ; une unité
 * nouvelle s'affiche telle quelle (« cageot », « douzaine ») sans modification
 * du code.
 */
export type PriceUnit = string;

const UNIT_LABELS: Record<string, string> = {
  kg: 'kg',
  piece: 'pièce',
  litre: 'L',
  botte: 'botte',
  bouteille: 'bouteille',
  pot: 'pot',
  sachet: 'sachet',
  pack: 'pack',
};

export function unitLabel(unit: PriceUnit): string {
  return UNIT_LABELS[unit] ?? unit;
}

export interface Product {
  readonly id: string;
  readonly name: string;
  readonly category: CategoryId;
  readonly priceCents: number;
  readonly unit: PriceUnit;
  /**
   * Remise anti-gaspi en pourcentage entier, absente pour un produit ordinaire.
   * Sa présence est ce qui fait apparaître le chip or récolte.
   */
  readonly antiWastePercent?: number;
  readonly sponsored?: boolean;
}

export interface Producer {
  readonly id: string;
  readonly name: string;
  readonly commune: string;
  /** Point de retrait. La distance en est déduite, elle n'est pas stockée. */
  readonly coordinates: Coordinates;
  /** Rang de 1 à 10 dans la grille des paliers. */
  readonly tierLevel: number;
  readonly reviewCount: number;
  /** Moyenne sur 5, masquée sous trois avis par `ct-rating`. */
  readonly ratingAverage?: number;
  /** Créneau de retrait, annoncé avant toute commande. */
  readonly pickup: string;
  /**
   * Terminal carte sur place. À `false` par défaut côté producteur (lot 2) :
   * l'annoncer évite le malentendu au retrait.
   */
  readonly cardAccepted: boolean;
  readonly achievements: readonly string[];
  readonly products: readonly Product[];
}

/**
 * Emplacement « En vedette dans votre secteur ».
 *
 * Le produit voyage avec son producteur : mis en avant sans dire chez qui, il
 * n'apprend rien au client et ne vaut rien pour le producteur qui l'a payé.
 */
export interface FeaturedSlot {
  readonly producer: Producer;
  readonly product: Product;
  readonly sponsored: boolean;
}

/**
 * Rayon d'un secteur de vente, en kilomètres.
 *
 * Valeur provisoire, à arrêter avec le Product Owner terrain : elle détermine à
 * la fois la portée d'une mise en avant et le nombre de créneaux vendables. Le
 * dossier limite ces créneaux par secteur, ce qui suppose que le secteur ait une
 * étendue définie.
 */
export const SECTOR_RADIUS_KM = 30;

/**
 * Paliers de distance maximale, affichés une fois le GPS activé.
 *
 * Le plancher à 5 km évite un rayon de quelques centaines de mètres, peu
 * réaliste en circuit court rural. Les paliers montent par approximations
 * doublantes jusqu'à 100 km pour couvrir un département entier.
 */
export const DISTANCE_RADIUS_OPTIONS = [5, 10, 20, 30, 50, 100] as const;

export type DistanceRadiusKm = (typeof DISTANCE_RADIUS_OPTIONS)[number];

/** Rayon par défaut à l'activation du GPS : aligné sur le secteur de mise en avant. */
export const DEFAULT_MAX_DISTANCE_KM: DistanceRadiusKm = 30;

export function formatDistanceRadius(km: DistanceRadiusKm): string {
  return `${km} km`;
}

/** Valeur sentinelle du filtre distance : aucun plafond, tri géographique conservé. */
export type DistanceFilter = DistanceRadiusKm | null;

export function formatDistanceFilter(km: DistanceFilter): string {
  return km === null ? 'Sans limite' : formatDistanceRadius(km);
}

/**
 * Produits montrés d'emblée sur une carte de producteur en consultation libre.
 *
 * Trois lignes suffisent à donner le genre de la ferme, et surtout elles
 * donnent la même hauteur à toutes les cartes : une grille où un producteur
 * occupe trois fois la place de son voisin se lit comme un classement qu'on
 * n'a pas voulu. Le reste de la gamme est à un clic, jamais perdu.
 *
 * Pendant une recherche, la troncature ne s'applique pas : cacher le
 * quatrième résultat correspondant serait cacher ce qui est cherché.
 */
export const SHOWCASE_PRODUCT_COUNT = 3;

/**
 * Le client est-il dans le secteur de ce producteur ?
 *
 * Sans position, la réponse est non et non « peut-être ». C'est ce qui interdit
 * d'afficher une mise en avant « dans votre secteur » à un visiteur dont on
 * ignore où il se trouve : le créneau est vendu pour un secteur donné, l'exposer
 * ailleurs ne sert ni le client, qui verra un producteur hors de portée, ni le
 * producteur, qui a payé une visibilité locale.
 */
export function isWithinSector(
  position: Coordinates | undefined,
  target: Coordinates,
): position is Coordinates {
  return position !== undefined && distanceBetween(position, target) <= SECTOR_RADIUS_KM;
}

/**
 * Producteur tel qu'affiché dans une liste de résultats.
 *
 * La distance est un type à part et non un champ du producteur : elle dépend de
 * qui regarde, et elle n'existe pas tant que le client n'a pas donné sa
 * position. Un `number` par défaut aurait forcé une valeur inventée.
 */
export interface ProducerNearby extends Producer {
  /** Absente tant que la position du client est inconnue. */
  readonly distanceKm: number | undefined;
  /**
   * La recherche portait sur l'identité du producteur — son nom ou sa commune —
   * et non sur un produit.
   *
   * Toujours `false` hors recherche : l'écran s'en sert pour annoncer « c'est ce
   * producteur que vous cherchiez », ce qui n'a de sens qu'en réponse à une
   * saisie. La distinction vaut aussi pour la gamme affichée, conservée entière
   * dans ce cas.
   */
  readonly matchedByName: boolean;
}

export interface SearchCriteria {
  readonly query: string;
  /** `undefined` signifie « toutes les catégories ». */
  readonly category: CategoryId | undefined;
  readonly antiWasteOnly: boolean;
  /** Position du client, `undefined` tant qu'il ne l'a pas partagée. */
  readonly position: Coordinates | undefined;
  /**
   * Distance maximale en kilomètres, appliquée seulement lorsque la position
   * est connue. `null` signifie sans limite : tout est listé, toujours classé
   * par distance. `undefined` tant que la position est inconnue.
   */
  readonly maxDistanceKm: number | null | undefined;
}

/** Étiquette de prix affichée au client (prix anti-gaspi s’il s’applique). */
export function formatPrice(product: Product): string {
  const cents = effectivePriceCents(product);
  return `${formatEurFromCents(cents)}/${unitLabel(product.unit)}`;
}

/** Prix catalogue hors remise, pour le barré anti-gaspi. */
export function formatRegularPrice(product: Product): string {
  return `${formatEurFromCents(product.priceCents)}/${unitLabel(product.unit)}`;
}

export function hasAntiWastePrice(product: Product): boolean {
  return product.antiWastePercent !== undefined && product.antiWastePercent > 0;
}

export function effectivePriceCents(product: Product): number {
  if (!hasAntiWastePrice(product)) {
    return product.priceCents;
  }
  const capped = Math.min(product.antiWastePercent!, 90);
  return Math.max(0, Math.round((product.priceCents * (100 - capped)) / 100));
}

/**
 * Rayons présents dans une liste d'identifiants, connus d'abord (ordre du
 * dictionnaire), puis ceux qu'on n'avait pas encore nommés.
 */
export function categoriesFromIds(ids: readonly CategoryId[]): Category[] {
  const sold = [...new Set(ids)];
  const known = CATEGORIES.filter((category) => sold.includes(category.id));
  const extras = sold
    .filter((id) => !CATEGORIES.some((category) => category.id === id))
    .map((id) => ({ id, label: categoryLabel(id) }));
  return [...known, ...extras];
}

/** Catégories effectivement vendues par un producteur. */
export function producerCategories(producer: Producer): readonly Category[] {
  return categoriesFromIds(producer.products.map((product) => product.category));
}

export interface CategoryGroup {
  readonly category: Category;
  readonly products: readonly Product[];
}

/**
 * Gamme d'un producteur rangée par rayon, dans l'ordre des filtres.
 *
 * Une gamme complète se lit par rayon et non à la file : c'est ainsi qu'elle
 * est présentée sur un étal, et la fiche est le seul écran qui l'affiche
 * entière. Aucun rayon vide n'apparaît, puisque les rayons sont déduits des
 * produits.
 */
export function groupProductsByCategory(producer: Producer): readonly CategoryGroup[] {
  return producerCategories(producer).map((category) => ({
    category,
    products: producer.products.filter((product) => product.category === category.id),
  }));
}

/**
 * Filtres du catalogue : uniquement ce qui est en vente, pour ne pas afficher
 * un rayon vide, tout en laissant apparaître un rayon nouveau dès le premier
 * produit.
 */
export function catalogCategories(producers: readonly Producer[]): readonly Category[] {
  return categoriesFromIds(
    producers.flatMap((producer) => producer.products.map((p) => p.category)),
  );
}

/**
 * Repli sur une comparaison insensible à la casse et aux accents.
 *
 * Sans cela « legumes » ne trouverait pas « Légumes » et « oeufs » ne trouverait
 * pas « Œufs » : les deux saisies les plus probables au clavier.
 */
function fold(value: string): string {
  return (
    value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      // Avant la substitution des ligatures, sans quoi « Œufs » garderait sa
      // majuscule et ne correspondrait plus à la saisie « oeufs ».
      .toLowerCase()
      .replace(/œ/g, 'oe')
      .replace(/æ/g, 'ae')
      .trim()
  );
}

const BY_NAME = new Intl.Collator('fr-FR', { sensitivity: 'base' });

/**
 * Producteurs correspondant aux critères.
 *
 * Chaque producteur retenu ne conserve que ses produits correspondants : un
 * client qui cherche « fraise » veut voir les fraises, pas la gamme complète
 * d'un producteur qui en vend. Quand la recherche porte sur le nom du
 * producteur ou de sa commune, toute la gamme est conservée — c'est alors le
 * producteur qui est cherché, pas un produit.
 *
 * Le classement est géographique dès que la position est connue, alphabétique
 * sinon. Pas de point de référence par défaut : classer « du plus proche » à
 * partir du centre d'une commune choisie par nous afficherait des distances
 * fausses avec l'aplomb des vraies.
 *
 * Aucun emplacement payé ne remonte ici. Les mises en avant achetées vivent
 * dans leur propre encart, signalées comme telles, sinon « le plus proche »
 * cesserait de vouloir dire quelque chose.
 */
export function searchCatalog(
  producers: readonly Producer[],
  criteria: SearchCriteria,
): readonly ProducerNearby[] {
  const needle = fold(criteria.query);
  const { position, maxDistanceKm } = criteria;

  return producers
    .map((producer) => {
      // Distinct de `producerMatches` : une recherche vide retient tout le monde
      // sans que personne n'ait été « cherché par son nom ».
      const matchedByName =
        needle.length > 0 &&
        (fold(producer.name).includes(needle) || fold(producer.commune).includes(needle));
      const producerMatches = needle.length === 0 || matchedByName;

      const products = producer.products.filter((product) => {
        if (criteria.category !== undefined && product.category !== criteria.category) {
          return false;
        }
        if (criteria.antiWasteOnly && product.antiWastePercent === undefined) {
          return false;
        }
        return producerMatches || fold(product.name).includes(needle);
      });

      const distanceKm =
        position === undefined ? undefined : distanceBetween(position, producer.coordinates);

      return { ...producer, products, distanceKm, matchedByName };
    })
    .filter((producer) => {
      if (producer.products.length === 0) {
        return false;
      }
      if (
        position !== undefined &&
        maxDistanceKm !== undefined &&
        maxDistanceKm !== null &&
        producer.distanceKm !== undefined &&
        producer.distanceKm > maxDistanceKm
      ) {
        return false;
      }
      return true;
    })
    .sort((left, right) =>
      left.distanceKm !== undefined && right.distanceKm !== undefined
        ? left.distanceKm - right.distanceKm
        : BY_NAME.compare(left.name, right.name),
    );
}

/** Nombre de produits visibles, pour annoncer le volume du résultat. */
export function countProducts(producers: readonly ProducerNearby[]): number {
  return producers.reduce((total, producer) => total + producer.products.length, 0);
}
